import React, { useState } from 'react';
import { TableRow } from '@material-ui/core';
import { isNumber } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { CategoriesStatisticsQuery, Color } from '../../../../generated/graphql';
import { DEFAULT_TABLE_ROW_HEIGHT, LoaderRow, TableCell } from '../../../../ui-kit';
import { useUser } from '../../../../utils/hooks/auth';
import { usePriceService } from '../../../../utils/hooks/priceService';
import NestedCollapsibleTable from '../NestedCollapsibleTable';
import { getHeadCells } from './utils';
import { Row } from './types';
import TableCellContent from './TableCellContent';

interface TableContentProps {
    isLoading?: boolean;
    items: CategoriesStatisticsQuery['categoriesStatistics'];
}

const TableContent = ({ isLoading, items }: TableContentProps) => {
    const { user: { venueType, defaultCurrency: { currencyCode } } } = useUser();
    const [collapseMap, setCollapseMap] = useState<Record<string, boolean>>({});
    const headCells = getHeadCells(venueType);
    const { priceService } = usePriceService();
    const { t } = useTranslation();

    const topLevelCategories: Row[] = [];
    const childCategoryMap: Record<string, Row[]> = {};

    items.forEach(({
        id, name, grossRevenue, quantity, foodCost, netMargin, categoryId, netRevenue, color, productWithNoRecipeCount, primeCost
    }) => {
        const row: Row = {
            id,
            name,
            grossRevenue: priceService.toLocaleString(grossRevenue, currencyCode),
            netRevenue: priceService.toLocaleString(netRevenue, currencyCode),
            quantity,
            foodCost: isNumber(foodCost) ? `${priceService.fractionToPercent(foodCost, 2)}%` : t('N/A'),
            netMargin: isNumber(netMargin) ? priceService.toLocaleString(netMargin, currencyCode) : t('N/A'),
            color: color ? Color[color as keyof typeof Color] : Color.Blue,
            productWithNoRecipeCount: productWithNoRecipeCount || 0,
            primeCost: isNumber(primeCost) ? priceService.toLocaleString(primeCost, currencyCode) : t('N/A'),
        };
        if (categoryId) {
            if (!childCategoryMap[categoryId]) {
                childCategoryMap[categoryId] = [];
            }
            childCategoryMap[categoryId].push(row);
        } else {
            topLevelCategories.push(row);
        }
    });

    const setOpen = (id: string) => {
        const updatedCollapseMap = { ...collapseMap, [id]: !collapseMap[id] };
        setCollapseMap(updatedCollapseMap);
    };

    const columnCount = headCells.length;

    if (isLoading) {
        return <LoaderRow columnCount={columnCount} itemsPerPage={10} itemHeight={DEFAULT_TABLE_ROW_HEIGHT} />;
    }

    return (
        <>
            {topLevelCategories.map((parentCategoryRow, topLevelIndex) => {
                const isParentRowEven = topLevelIndex % 2 === 0;
                const { color } = parentCategoryRow;

                return (
                    <React.Fragment key={parentCategoryRow.id}>
                        <TableRow className={isParentRowEven ? undefined : 'stripedRow'} tabIndex={-1} key={parentCategoryRow.id}>
                            {headCells.map(({ id, align }, index) => {
                                const handleSetIsOpen = () => setOpen(parentCategoryRow.id);
                                const hasCollapsibleContent = !!childCategoryMap[parentCategoryRow.id];
                                const isOpen = collapseMap[parentCategoryRow.id];

                                return (
                                    <TableCell key={`${parentCategoryRow.id}-${id}`} isSticky={index === 0} align={align}>
                                        <TableCellContent
                                            id={id}
                                            color={color}
                                            hasCollapsibleContent={hasCollapsibleContent}
                                            isOpen={isOpen}
                                            setIsOpen={handleSetIsOpen}
                                            content={parentCategoryRow[id]}
                                            productWithNoRecipeCount={parentCategoryRow.productWithNoRecipeCount}
                                            align={align}
                                        />

                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        {childCategoryMap[parentCategoryRow.id]
                            ? (
                                <TableRow tabIndex={-1}>
                                    <TableCell padding='none' style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
                                        <NestedCollapsibleTable
                                            isOpen={collapseMap[parentCategoryRow.id]}
                                            stripeColor={color}
                                            startOdd={!isParentRowEven}
                                        >
                                            {childCategoryMap[parentCategoryRow.id].map(childCategoryRow => (
                                                <TableRow key={childCategoryRow.id} tabIndex={-1}>
                                                    {headCells.map(({ id, align }, index) => {
                                                        const noRecipeProductCount = childCategoryRow.productWithNoRecipeCount;
                                                        return (
                                                            <TableCell
                                                                key={`${childCategoryRow.id}-${id}`}
                                                                isSticky={index === 0}
                                                                align={align}
                                                            >
                                                                <TableCellContent
                                                                    id={id}
                                                                    isChild
                                                                    content={childCategoryRow[id]}
                                                                    color={color}
                                                                    productWithNoRecipeCount={noRecipeProductCount}
                                                                    align={align}
                                                                />
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </NestedCollapsibleTable>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                    </React.Fragment>
                );
            })}
        </>
    );

};

export default TableContent;